class NewsletterForm extends HTMLElement {
  constructor() {
    super();
    this.newsletterKey = Theme.keys.newsletter_key;

    this.newsletterContainer = this.parentElement?.parentElement?.parentElement;
    this.footerElement = null;
    this.observer = null;
    this.closeButton = null;
    this.isInitialized = false;
    this.cookieBannerObserver = null;
    this.cookieBannerFallbackTimer = null;
    this.hasObservedCookieBanner = false;
    this.handleClose = this.handleClose.bind(this);
    this.handleConsentCollected = this.handleConsentCollected.bind(this);
    this.handleCookieBannerChange = this.handleCookieBannerChange.bind(this);
  }

  connectedCallback() {
    if (this.isNewsletterClosed()) {
      this.newsletterContainer.classList.add("view-desktop:hidden");
      return;
    }

    this.closeButton = this.querySelector("button[data-dialog-close]");

    if (this.closeButton) {
      this.closeButton.addEventListener("click", this.handleClose);
    }

    this.footerElement = document.querySelector("footer-element");

    if (!this.footerElement) {
      console.warn(
        "Footer element not found. Newsletter form will not function properly.",
      );
      return;
    }

    this.initializeWhenCookieBannerClosed();
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }

    if (this.closeButton) {
      this.closeButton.removeEventListener("click", this.handleClose);
    }

    document.removeEventListener(
      "visitorConsentCollected",
      this.handleConsentCollected,
    );
    this.stopCookieBannerObserver();
  }

  handleClose() {
    localStorage.setItem(this.newsletterKey, Date.now().toString());

    this.classList.add("hidden");

    this.newsletterContainer.classList.add("view-desktop:hidden");
  }

  isNewsletterClosed() {
    return localStorage.getItem(this.newsletterKey) !== null;
  }

  setupIntersectionObserver() {
    this.observer = new IntersectionObserver((entries) => {
      const [{ isIntersecting }] = entries;

      this.classList.toggle("hidden!", isIntersecting);
    });

    this.observer.observe(this.footerElement);
  }

  async initializeWhenCookieBannerClosed() {
    if (await this.isCookieBannerVisible()) {
      document.addEventListener(
        "visitorConsentCollected",
        this.handleConsentCollected,
        { once: true },
      );
      this.watchCookieBanner();
      return;
    }

    this.initializeNewsletter();
  }

  handleConsentCollected() {
    this.stopCookieBannerObserver();
    this.initializeNewsletter();
  }

  watchCookieBanner() {
    this.cookieBannerObserver = new MutationObserver(
      this.handleCookieBannerChange,
    );

    this.cookieBannerObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style", "hidden"],
    });

    this.handleCookieBannerChange();

    this.cookieBannerFallbackTimer = window.setTimeout(() => {
      if (!this.hasObservedCookieBanner) {
        this.stopCookieBannerObserver();
        this.initializeNewsletter();
      }
    }, 1000);
  }

  handleCookieBannerChange() {
    const cookieBanner = document.querySelector("#shopify-pc__banner");
    const isBannerRendered = Boolean(cookieBanner?.getClientRects().length);

    if (isBannerRendered) {
      this.hasObservedCookieBanner = true;
      return;
    }

    if (this.hasObservedCookieBanner) {
      this.stopCookieBannerObserver();
      this.initializeNewsletter();
    }
  }

  stopCookieBannerObserver() {
    if (this.cookieBannerObserver) {
      this.cookieBannerObserver.disconnect();
      this.cookieBannerObserver = null;
    }

    if (this.cookieBannerFallbackTimer) {
      window.clearTimeout(this.cookieBannerFallbackTimer);
      this.cookieBannerFallbackTimer = null;
    }
  }

  initializeNewsletter() {
    if (this.isInitialized || this.isNewsletterClosed()) {
      return;
    }

    this.isInitialized = true;
    this.setupIntersectionObserver();
  }

  async isCookieBannerVisible() {
    if (!window.Shopify?.customerPrivacy?.shouldShowBanner) {
      await new Promise((resolve) => {
        if (!window.Shopify?.loadFeatures) {
          resolve();
          return;
        }

        window.Shopify.loadFeatures(
          [{ name: "consent-tracking-api", version: "0.1" }],
          resolve,
        );
      });
    }

    return Boolean(window.Shopify?.customerPrivacy?.shouldShowBanner?.());
  }
}

customElements.define("newsletter-form", NewsletterForm);
